/**
 * @fileoverview The server-derived event CTA contract (API-1616 / spec §2.3, D6).
 *
 * One field, derived once on the server, rendered everywhere. Before this, each
 * surface branched on `ticketUrl` alone behind a hardcoded "Get tickets" label —
 * so a free community event, a sold-out show, and a cancelled one all offered
 * the same button. The 2026-07-17 pipeline audit filed that as the S5 dishonesty
 * class.
 *
 * The derivation rules live in exactly one module
 * (`services/backend-api/src/services/eventCta.ts`); this file only owns the
 * wire vocabulary so the backend, web app, and tests agree on the spelling.
 *
 * Per D6 there is deliberately **no** neutral / `details-only` member: an event
 * that cannot produce one of these five has no price story, and the contract's
 * answer is to omit the CTA entirely (and, from PIP-167, to quarantine the row)
 * rather than to surface a noncommittal button.
 *
 * @since API-1616 (Ingestion v2 — Wave 1, surface honesty)
 */

/**
 * The affordance a surface should render for an event.
 *
 * This is the **action**, not the status label. Lifecycle state travels
 * separately on `status` (API-1614/API-1615), so a UI renders "Postponed" from
 * `status` while `ctaType` only says "there is nothing to buy here". That split
 * is why three distinct non-purchasable statuses collapse onto `cancelled`
 * without lying to the user — see `NON_PURCHASABLE_CTA_STATUSES` in the
 * derivation module.
 *
 * - `buy`        — a live, purchasable provider listing exists. Legitimate even
 *                  when the exact price was never published (D6: the listing is
 *                  the price story; the provider page shows the real number).
 * - `rsvp`       — registration, not purchase. Only from an explicit RSVP signal.
 * - `free-entry` — free admission. Only from an explicit free signal, never from
 *                  a missing price (that fabrication is exactly what API-1614
 *                  deleted).
 * - `sold-out`   — capacity reached. Non-actionable.
 * - `cancelled`  — the event will not happen as listed, or its sales have ended.
 *                  Non-actionable.
 * - `resale`     — (PIP-343) a Ticketmaster resale (`tmr`) listing with no
 *                  primary purchase path we can point at. `ctaHref` is TM's
 *                  resale-marketplace page, which 404s whenever no resale
 *                  listings exist, so surfaces label it
 *                  {@link RESALE_CTA_LABEL} and NEVER "Get tickets". A resale row that
 *                  has a venue box office or venue website gets `buy` with
 *                  that destination instead.
 */
export type CtaType = 'buy' | 'rsvp' | 'free-entry' | 'sold-out' | 'cancelled' | 'resale';

/**
 * PIP-343 — the ONE spelling of the `resale` CTA label (operator decision
 * 2026-10-03). Every surface renders it from here — the server decision panel,
 * the web label maps, the home brief — so the copy cannot drift. Web surfaces
 * may append their own `→` affordance; the words stay this constant.
 */
export const RESALE_CTA_LABEL = 'Resale on Ticketmaster';

/**
 * The resolved CTA for an event: what to render, and where it points.
 *
 * `ctaHref` is `null` whenever there is no honest destination — a non-actionable
 * `ctaType`, a link the liveness sweeper marked `dead` (PIP-163), or a
 * free-entry event that simply has no page. Consumers must render a disabled
 * affordance in that case and never a dead `#`.
 */
export interface EventCta {
  readonly ctaType: CtaType;
  readonly ctaHref: string | null;
}
